import * as React from 'react';
import {useEffect, useState} from 'react';
import {clsx} from 'clsx';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {InputSelect} from '@/src/app/data/types/InputTypes';
import {Input} from '../ui/Input/Input';
import {Button} from '../ui/shadcn-components/button';
import {SelectBox} from '../ui/SelectBox/SelectBox';
import {State} from '../../app/data/general/state.enum';
import {translate} from '../../app/shared/utils/localization-utils';
import {getAresData} from '../../app/service/ares.service';
import {getExistingClient, getExistingUser, submitFormData} from '../../app/service/registration.service';
import {useNotification} from '../../app/service/notification.service';
import {NotificationLevel} from '../../app/data/general/notificationLevel.enum';
import SubTitle from '../../app/shared/layout/subtitle/subTitle';
import Title from '../../app/shared/layout/title/title';
import InfoBox from '../ui/InfoBox/InfoBox';
import TextModalPanel from '../../app/shared/layout/text-modal-panel/text-modal-panel';

interface IRegisterForm {
	className?: string;
	legalFormsData: InputSelect[];
	aresData?: any;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const registrationFormSchema = z.object({
	legalForm: z.string({required_error: translate('error.required')}),
	ico: z.string().optional(),
	officialName: z.string({required_error: translate('error.required')}).min(1, translate('error.required')),
	street: z.string({required_error: translate('error.required')}).min(1, translate('error.required')),
	houseNumber: z.string({required_error: translate('error.required')}).min(1, translate('error.required')),
	orientationNumber: z.string().optional(),
	municipality: z.string({required_error: translate('error.required')}).min(1, translate('error.required')),
	zipCode: z.string({required_error: translate('error.required')}).min(1, translate('error.required')),
	state: z.string({required_error: translate('error.required')}),
	firstName: z.string({required_error: translate('error.required')}).min(1, translate('error.required')),
	lastName: z.string({required_error: translate('error.required')}).min(1, translate('error.required')),
	username: z.string({required_error: translate('error.required')})
		.regex(/^[a-z0-9_]+$/, translate('error.validation.username'))
		.min(5, translate('error.validation2.min', true, 'registration.loginName', 5)),
	email: z.string({required_error: translate('error.required')}).nonempty(translate('error.required')).email(translate('error.invalidEmail'))
}).superRefine((data, ctx) => {
	const isIcoRequired = ![
		'107',
		'TA1',
		'000'
	].includes(data.legalForm);

	if (isIcoRequired) {
		if (!data.ico || data.ico.length !== 8 || !/^\d{8}$/.test(data.ico)) {
			ctx.addIssue({
				path: ['ico'],
				code: z.ZodIssueCode.custom,
				message: translate('error.validation.invalidIco')
			});
		}
	}
});

export type RegistrationFormData = z.infer<typeof registrationFormSchema>

export default function RegisterForm({
	className,
	legalFormsData
}: IRegisterForm) {
	const {
		register,
		watch,
		setValue,
		setError,
		handleSubmit,
		control,
		reset,
		clearErrors,
		formState: {errors}
	} = useForm<RegistrationFormData>({
		resolver: zodResolver(registrationFormSchema),
		defaultValues: {legalForm: '000'}
	});

	const [isClientRegistred, setIsClientRegistred] = useState(false);
	const [isUsernameExisted, setIsUsernameExisted] = useState(false);
	const [isModalOpen, setModalOpen] = useState(false);
	const icoValue: string = watch('ico');
	const legalValue: string = watch('legalForm');
	const usernameValue: string = watch('username');
	const exitedUsernameMsg: string = translate('chyba.prihlasovaciJmenoNeniDostupne');
	const subjectNotFoundMsg: string = translate('chyba.subjektNenalezen');
	const intenalErrorMsg: string = translate('error.internalServerError');
	let isIcoRequired: boolean = true;
	let taZadavatelUrl = process.env.REACT_APP_URL_TA_ZADAVATEL;

	const {notify} = useNotification();

	switch (legalValue) {
		case '107':
		case 'TA1':
		case '000':
			isIcoRequired = false;
			break;
	}
	const icoValidate = (ico: string): boolean => {
		if (ico.length !== 8) {
			setError('ico', {
				type: 'manual',
				message: translate('error.validation.invalidIco')
			});
			return false;
		}

		if (!/^\d{8}$/.test(ico)) {
			setError('ico', {
				type: 'manual',
				message: translate('error.validation.invalidIco')
			});
			return false;
		}

		clearErrors('ico');
		return true;
	};

	const onSubmit = async(formData: RegistrationFormData) => {
		if (isClientRegistred) {
			const msg = translate('chyba.zadavatelSIcJizExistuje', true, icoValue);
			notify(
				NotificationLevel.WARNING,
				msg
			);
			return;
		}

		if (isUsernameExisted) {
			notify(
				NotificationLevel.WARNING,
				exitedUsernameMsg
			);
			return;
		}

		const response: any = await submitFormData(formData);

		if (!response.ok) {
			const msg: string = translate(response.klic, true, response.parametry[0]);
			notify?.(NotificationLevel.ERROR, msg);
		} else {
			setModalOpen(true);
			reset();
		}
	};

	const onError = (): void => {
		if (isIcoRequired && icoValue.length !== 8) {
			icoValidate(icoValue);
		} else if (icoValue && icoValue.length > 0 && icoValue.length < 8) {
			icoValidate(icoValue);
		}
	};

	const handleGetAresData = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();

		getAresData(icoValue).then((res) => {
			if (!res?.response?.ok) {
				res?.response?.status === 404 ? notify?.(NotificationLevel.ERROR, subjectNotFoundMsg) : notify?.(NotificationLevel.ERROR, intenalErrorMsg);
				return;
			}

			if (res.data.sidlo) {
				setValue('street', res.data.sidlo.nazevUlice, {shouldValidate: true});
				setValue('houseNumber', String(res.data.sidlo.cisloDomovni), {shouldValidate: true});
				setValue('orientationNumber', String(res.data.sidlo.cisloOrientacni ?? ''));
				setValue('municipality', res.data.sidlo.nazevObce, {shouldValidate: true});
				setValue('zipCode', String(res.data.sidlo.psc), {shouldValidate: true});
				setValue('state', res.data.sidlo.kodStatu, {shouldValidate: true});
				setValue('legalForm', res.data.pravniForma, {shouldValidate: true});
				setValue('officialName', res.data.obchodniJmeno, {shouldValidate: true});
				setValue('ico', icoValue, {shouldValidate: true});
			}
		});
	};

	const handleGetExistignClient = (ico: string) => {
		getExistingClient(ico).then((response) => {
			if (!response.ok) {
				const msg = translate(response.klic, true, response.parametry[0]);
				notify?.(NotificationLevel.ERROR, msg);
			} else {
				if (response.existuje === true) {
					setIsClientRegistred(true);
					const msg = translate('chyba.zadavatelSIcJizExistuje', true, icoValue);
					notify(NotificationLevel.WARNING, msg);
				} else {
					notify(NotificationLevel.DESTROY, '');
					setIsClientRegistred(false);
				}
			}
		});
	};

	const handleGetExistignUser = (username: string) => {
		getExistingUser(username).then((response) => {
			if (!response.ok) {
				const msg = translate(response.klic, true, response.parametry[0]);
				notify?.(NotificationLevel.ERROR, msg);
			} else {
				if (response.existuje === true) {
					setIsUsernameExisted(true);
					notify(NotificationLevel.WARNING, exitedUsernameMsg);
				} else {
					notify(NotificationLevel.DESTROY, '');
					setIsUsernameExisted(false);
				}
			}
		});
	};

	useEffect(() => {
		if (icoValue) {
			icoValidate(icoValue);
			const isIcoValid = icoValidate(icoValue);

			if (isIcoValid) {
				handleGetExistignClient(icoValue);
			}
		}

		if (!icoValue || icoValue.length === 0) {
			setIsClientRegistred(false);
		}
		if (!icoValue || icoValue.length === 0 && isIcoRequired === false) {
			clearErrors('ico');
		}

	}, [icoValue]);

	useEffect(() => {
		if (usernameValue && usernameValue.length >= 5) {
			handleGetExistignUser(usernameValue);
		}

		if (!usernameValue || usernameValue.length === 0) {
			setIsUsernameExisted(false);
		}
	}, [usernameValue]);

	useEffect(() => {
		if (isIcoRequired === false) {
			clearErrors('ico');
		}
	}, [legalValue]);

	return (
		<div className="relative bg-white m-6 p-6 ">
			<Title customClass="" keyValue={'registration.title'}/>
			<form className={clsx('flex flex-col', className)} onSubmit={handleSubmit(onSubmit, onError)}>
				<SubTitle customClass="" keyValue={'registration.identificationInformation'}/>
				<div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_300px] lg:grid-cols-3 gap-[22px] md:gap-x-5 md:gap-y-[30px]">
					<Controller
						control={control}
						name="legalForm"
						render={({field}) => (
							<SelectBox
								className="md:col-start-1 md:col-end-3"
								value={field.value}
								onChange={field.onChange}
								placeholder={translate('registration.legalForm')}
								error={errors.legalForm?.message}
								selectData={legalFormsData}
								isRequired={true}/>
						)}
					/>
					<div className="relative">
						<Input register={register('ico', {
							onBlur: () => {
								onError();
							}
						})}
							   className={''}
							   text={translate('registration.ico')}
							   error={errors.ico?.message}
							   name="ico"
							   isRequired={isIcoRequired}/>
						<Button className="absolute top-[10px] right-[10px] text-xs lg:text-base px-2 lg:px-4"
								disabled={icoValue?.length !== 8}
								variant="secondary"
								onClick={handleGetAresData}>{translate('registration.prefillFromIco')}</Button>
					</div>
					<Input className="md:col-span-3"
						   register={register('officialName')}
						   text={translate('registration.officialName')}
						   error={errors.officialName?.message}
						   name="officialName"
						   isRequired={true}/>
				</div>
				<SubTitle customClass="" keyValue={'registration.headquartersAndContacts'}/>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-[22px] md:gap-x-5 md:gap-y-[30px]">
					<Input register={register('street')}
						   text={translate('registration.street')}
						   error={errors.street?.message} name="street"
						   isRequired={true}/>
					<Input register={register('houseNumber')}
						   text={translate('registration.houseNumber')}
						   error={errors.houseNumber?.message}
						   name="houseNumber"
						   isRequired={true}/>
					<Input register={register('orientationNumber')}
						   text={translate('registration.orientationNumber')}
						   error={errors.orientationNumber?.message}
						   name="orientationNumber"/>
					<Input register={register('municipality')}
						   text={translate('registration.city')}
						   error={errors.municipality?.message}
						   name="municipality"
						   isRequired={true}/>
					<Input register={register('zipCode')}
						   text={translate('registration.zipCode')}
						   error={errors.zipCode?.message} name="zipCode"
						   isRequired={true}/>
					{Object.values(State) && Object.values(State).length > 0 &&
						<Controller control={control}
									name="state"
									render={({field}) => (
										<SelectBox
											value={field.value}
											onChange={field.onChange}
											placeholder={translate('registration.state')}
											error={errors.state?.message}
											selectData={Object.values(State)}
											isRequired={true}
										/>
									)}
						/>
					}

				</div>
				<SubTitle customClass="" keyValue={'registration.userInformation'}/>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-[22px] md:gap-x-5 md:gap-y-[30px]">
					<Input register={register('username')}
						   text={translate('registration.loginName')}
						   error={errors.username?.message} name="username"
						   isRequired={true}/>
					<Input register={register('email')}
						   text={translate('registration.email')}
						   error={errors.email?.message} name="email"
						   isRequired={true}/>
					<Input register={register('firstName')}
						   text={translate('kontaktniOsobaZakazky.krestniJmeno')}
						   error={errors.firstName?.message} name="firstName"
						   isRequired={true}/>
					<Input register={register('lastName')}
						   text={translate('registration.lastName')}
						   error={errors.lastName?.message} name="lastName"
						   isRequired={true}/>
				</div>
				<div className="flex flex-col items-end gap-[22px] md:gap-x-5 md:gap-y-[30px] mt-8">
					<InfoBox className={'w-full'}>
						<div className={''}>
							Po kliknutí na tlačítko „Registrovat“ obdržíte e-mail s odkazem pro nastavení hesla. Po přihlášení se Vám zobrazí pokyny k
							dokončení
							registrace (pro využívání nástroje Tender arena v plném rozsahu dle <a className={'underline hover:opacity-70'}
																								   target="_blank"
																								   href="/documents/vop_v8_v1_compressed.pdf">Všeobecných
							obchodních podmínek</a>). Registraci lze dokončit
							elektronicky (datovou schránkou nebo s využitím elektronického podpisu) či v listinné podobě (poštou). V případě dotazů
							kontaktujte
							infolinku <a className={'font-semibold hover:opacity-70'} href="tel:+420226258888">+420 226 258 888</a> (pracovní dny 8–17
							h) nebo
							e-mail <a className={'font-semibold hover:opacity-70'} href="mailto:support@tendersystems.cz">support@tendersystems.cz</a>.
						</div>
					</InfoBox>
					<Button className={'max-w-fit'} type="submit" variant="default">{translate('buttonRegister')}</Button>
				</div>
			</form>
			<TextModalPanel
				open={isModalOpen}
				hasLink={taZadavatelUrl}
				confirmLabel={'buttonSave'}
				cancelLabel={'buttonClose'}
				title={translate('error.successRegistrationTitle')}
				textValue={translate('error.successRegistration')}></TextModalPanel>
		</div>
	);
}
